import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  ButtonGroup,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from "reactstrap";

interface DropdownProps {
  options?: any[];
  onSelect: (value: string) => void;
  defaultLabel?: string;
  width?: string;
  height?: string;
  backgroundColor?: string;
  DropdownHeading?: string;
  isShowHeading?: boolean;
  headingColor?: string;
  onLoadMore?: () => void;
  onSearch?: (searchTerm: string) => void; // ✅ NEW
  loading?: boolean;
  refresh?: boolean;
}

const RoundedDropdown: React.FC<DropdownProps> = ({
  options = [],
  onSelect,
  defaultLabel = "Select",
  width = "200px",
  height = "35px",
  backgroundColor = "info",
  DropdownHeading = "Heading",
  isShowHeading = true,
  headingColor,
  onLoadMore,
  onSearch, // ✅ NEW
  loading,
  refresh,
}) => {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<any>(defaultLabel);
  const [searchInput, setSearchInput] = useState(""); // ✅ NEW
  const observer = useRef<IntersectionObserver | null>(null);
  const lastItemRef = useRef<HTMLDivElement | null>(null);
  // const [defaultValue,setDefaultValue] = useState(defaultLabel);

  const toggle = () => setOpen(!open);

  const handleSelect = (option: any) => {
    setSelected(option.label);
    onSelect(option);
    setOpen(false);
  };

  useEffect(() => {
    // console.log("default label -->",selected)
    setSelected(defaultLabel);
  }, [refresh]);

  const observeLastItem = useCallback(
    (node: HTMLDivElement | null) => {
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && onLoadMore) {
            onLoadMore();
          }
        },
        { rootMargin: "0px", threshold: 1.0 }
      );

      if (node) observer.current.observe(node);
    },
    [onLoadMore]
  );

  useEffect(() => {
    if (lastItemRef.current) {
      observeLastItem(lastItemRef.current);
    }
  }, [options, observeLastItem]);

  // ✅ Debounced Search Handler
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (onSearch) onSearch(searchInput);
    }, 300);
    return () => clearTimeout(delayDebounce);
  }, [searchInput, onSearch]);

  return (
    <ButtonGroup>
      <Dropdown
        isOpen={open}
        toggle={toggle}
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
        }}
      >
        {isShowHeading && (
          <p
            style={{
              display: "flex",
              alignItems: "center",
              color: headingColor,
              marginRight: "5px",
              fontWeight: "500",
              justifyContent: "center",
              width: "100%",
            }}
          >
            {DropdownHeading}
          </p>
        )}
        <DropdownToggle
          color={backgroundColor}
          className="rounded-pill"
          // style={{
          //   // width: "fit-content",
          //   height,
          //   display: "flex",
          //   alignItems: "center",
          //   justifyContent: "space-between",
          //   width: "100%",
          // }}

          style={{
            display: "inline-flex", // ✅ shrink-wrap
            alignItems: "center",
            justifyContent: "space-between",
            minWidth: "max-content", // ✅ ensures full text fits
            height,
            padding: "0 12px", // ✅ add spacing
          }}
        >
          <p style={{ textWrap: "nowrap" }}>{selected}</p>
          <span
            style={{
              display: "inline-block",
              transition: "transform 0.3s",
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
              marginLeft: "5px",
              fontSize: "10px",
            }}
          >
            ▼
          </span>
        </DropdownToggle>

        <DropdownMenu style={{ width: width, padding: "0" }}>
          {onSearch && (
            <div className="px-2 pt-2">
              <input
                type="text"
                className="form-control"
                placeholder="Search..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                style={{ height: "30px", border: "1px solid #e0e0e0" }}
              />
            </div>
          )}

          <div style={{ maxHeight: "200px", overflowY: "auto" }}>
            {loading ? (
              <div className="text-center py-2">
                <div
                  className="spinner-border spinner-border-sm text-primary"
                  role="status"
                />
              </div>
            ) : options.length > 0 ? (
              options.map((option: any, index: number) => {
                const isLast = index === options.length - 1;
                return (
                  <DropdownItem
                    key={option.id || index}
                    onClick={() => handleSelect(option)}
                  >
                    <div
                      ref={isLast ? lastItemRef : null}
                      style={{
                        whiteSpace: "normal", // allow multi-line
                        wordBreak: "break-word", // break long words if needed
                        overflowWrap: "break-word", // ensures wrapping in all browsers
                        display: "block",
                        maxWidth: "100%",
                        lineHeight: "1.4",
                      }}
                    >
                      {option.label}
                    </div>
                  </DropdownItem>
                );
              })
            ) : (
              <div className="text-center text-muted py-2">
                No options found
              </div>
            )}
          </div>
        </DropdownMenu>
      </Dropdown>
    </ButtonGroup>
  );
};

export default RoundedDropdown;
